import type { UseQueryOptions } from '@tanstack/react-query';

import { useHistoricalPortfolioSnapshotQuery, useRangeDebugQuery } from '../queries';
import type { AsOfDateSetting } from './types';
import { getAllocationsAsOfDateTimestampFromSetting, getCurrentDate, getLastUploadedDate } from './utils';
import type { AnalysisSubject } from '../analysis';

type QueryOptions = Pick<UseQueryOptions, 'suspense' | 'enabled'>;

type Response = {
  rangeDebugResponse: ReturnType<typeof useRangeDebugQuery>;
  historicalPortfolioSnapshotResponse: ReturnType<typeof useHistoricalPortfolioSnapshotQuery>;
  allocationsAsOfDateTimestamp: number | undefined;
};

export const useHistoricalPortfolioSnapshot = ({
  analysisSubject,
  asOfDateSetting,
  options,
}: {
  analysisSubject: AnalysisSubject | undefined;
  asOfDateSetting: AsOfDateSetting | undefined;
  options?: QueryOptions;
}): Response => {
  const { enabled = true, suspense = false } = options ?? {};

  const rangeDebugResponse = useRangeDebugQuery(analysisSubject, {
    enabled: !!analysisSubject?.isHistoricalPortfolio && enabled,
    suspense,
  });

  const allocationsAsOfDateTimestamp = asOfDateSetting
    ? getAllocationsAsOfDateTimestampFromSetting({
        asOfDateSetting,
        currentDate: getCurrentDate(
          rangeDebugResponse.data?.historicalPortfolioMetadata?.allSortedAnalysisAllocationDates,
        ),
        lastUploadedDate: getLastUploadedDate(
          rangeDebugResponse.data?.historicalPortfolioMetadata?.allSortedRebalancingDates,
        ),
      })
    : undefined;

  const historicalPortfolioSnapshotResponse = useHistoricalPortfolioSnapshotQuery(
    {
      subject: analysisSubject,
      allocationsAsOfDate: allocationsAsOfDateTimestamp,
    },
    {
      enabled: !!analysisSubject?.isHistoricalPortfolio && !!allocationsAsOfDateTimestamp && enabled,
      suspense,
    },
  );

  return {
    rangeDebugResponse,
    historicalPortfolioSnapshotResponse,
    allocationsAsOfDateTimestamp,
  };
};
